import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Button, Icon, RichTextEditor } from '@innovationnorway/cl-react';
import { Kontakt, OneWayInOpportunityDto, SendEmailToCustomerDto } from 'src/api/v2';
import ToastContent from 'src/components/ToastContent/ToastContent';
import { useTranslation } from 'react-i18next';
import useCustomerEmail from '../../hooks/use-customer-email';
import { useCluster } from 'src/features/cluster';
import { useUser } from 'src/features/authorization';

type Props = {
  formId?: string;
  contactInfo?: Kontakt;
  threadId?: number;
  opportunity?: OneWayInOpportunityDto;
  onSent?: () => void;
};

const EmailComposer: React.FC<Props> = ({ formId, contactInfo, threadId, opportunity, onSent }) => {
  const { sendEmailMutation } = useCustomerEmail(threadId, formId);
  const { cluster } = useCluster();
  const { displayName } = useUser();
  const { t: tCommon } = useTranslation('common');
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tError } = useTranslation('error');
  const defaultBodyText = useMemo(
    () =>
      [
        `<p>Hei ${contactInfo?.fornavn} ${contactInfo?.etternavn},</p>`,
        `<p>Dette gjelder henvendelsen din ${opportunity?.title ?? ''}.</p>`,
        '<p><br /><br /><br /></p>',
        `<p>Med vennlig hilsen<br />${displayName ?? ''}<br />${cluster?.name ?? ''}</p>`,
      ].join(''),
    [cluster?.name, displayName, contactInfo?.etternavn, contactInfo?.fornavn, opportunity?.title],
  );
  const form = useForm<SendEmailToCustomerDto>({
    defaultValues: {
      bodyText: defaultBodyText,
    },
  });
  const [rawText, setRawText] = useState('');
  const [editorInstanceKey, setEditorInstanceKey] = useState(0);

  const handleOnSubmit = form.handleSubmit(async (input) => {
    if (!input.bodyText || !input.bodyText.length || !contactInfo?.epost) {
      return;
    }

    const data = {
      recipientEmail: contactInfo.epost,
      threadId,
      bodyText: input.bodyText,
      opportunityId: threadId ? undefined : opportunity?.id,
    } satisfies SendEmailToCustomerDto;

    await sendEmailMutation
      .mutateAsync({ dto: data })
      .then(() => {
        toast(
          <ToastContent
            title={tCommon('emailSent')}
            text={tCommon('emailSentDetails', { email: data.recipientEmail })}
            success
          />,
          {
            toastId: 'NoteAdded',
            autoClose: false,
            position: 'bottom-center',
          },
        );
        onSent?.();
        form.reset({ bodyText: '' });
        setEditorInstanceKey((currentKey) => currentKey + 1);
      })
      .catch(() =>
        toast(
          <ToastContent title={tCommon('updateFailed')} text={tCommon('emailNotSent')} success={false} />,
          {
            toastId: 'NoteAdded',
            autoClose: false,
            position: 'bottom-center',
          },
        ),
      );
  });

  return (
    <form onSubmit={handleOnSubmit} className="display--flex width--100">
      <div className="customer-communication-composer__wrapper">
        <Controller
          control={form.control}
          name="bodyText"
          rules={{
            validate: () => {
              if (!rawText.length) {
                return tError('validation.required');
              }

              if (rawText.length < 30) {
                return tError('validation.minValue', { value: 30 });
              }

              return true;
            },
          }}
          render={({ field }) => (
            <RichTextEditor
              key={editorInstanceKey} //This ensures the component is reset after an email has been sent
              className="customer-communication-composer__textarea"
              initialValue={field.value}
              label={tOneWayIn('communication.sendEmailToCustomer')}
              rows={window.innerWidth > 768 ? 8 : 2}
              onChangeRawText={(value) => setRawText(value)}
              onChange={(value) => field.onChange(value)}
              onBlur={field.onBlur}
            />
          )}
        />
        {form.formState.errors.bodyText?.message && (
          <div className="error-message">{form.formState.errors.bodyText.message}</div>
        )}
        <Button
          type="submit"
          aria-label={tCommon('send')}
          className="customer-communication-composer__button"
          disabled={sendEmailMutation.isLoading || !contactInfo?.epost}
        >
          <Icon prefix="fasr" iconName="paper-plane-top"></Icon>
        </Button>
      </div>
    </form>
  );
};

export default EmailComposer;
